import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Button,
  Typography,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress
} from "@mui/material";
import Navbar from "./NavigationTalent";
import api from "../api/api";
import { ENDPOINTS } from "../api/endpoints";

const NotificationPreferences = () => {
  const userId = localStorage.getItem("userId");

  const [form, setForm] = useState({
    userMail: "",
    userMail2: "",
    userCell: "",
    cellProvider: "",
    cityChoice: [],
    jobCategories: [],
    roleType: "",
    payStatus: "",
    userSex: "",
    ageFrom: "",
    ageTo: "",
    unionStatus: "",
    ethnicity: [],
    heightFrom: "",
    heightTo: "",
    weightFrom: "",
    weightTo: "",
    activeCell: true
  });

  const [locations, setLocations] = useState([]);
  const [ethnicities, setEthnicities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [
          locRes,
          ethRes,
          catRes,
          prefRes
        ] = await Promise.all([
          api.get(ENDPOINTS.getLocations()),
          api.get(ENDPOINTS.getEthnicities()),
          api.get(ENDPOINTS.getJobCategories()),
          api.get(ENDPOINTS.getUserNotificationPrefs({ userId }))
        ]);

        setLocations(locRes.data?.data || []);
        setEthnicities(ethRes.data?.data || []);
        setCategories(catRes.data?.data || []);

        const prefs = prefRes.data?.data || [];
        if (prefs.length) {
          const p = prefs[0];
          setForm(f => ({
            ...f,
            userMail: p.userEmail || "",
            userMail2: p.userEmail2 || "",
            userCell: p.userCell || "",
            cellProvider: p.cellProvider || "",
            cityChoice: p.cityChoice ? p.cityChoice.match(/.{1,3}/g) : [],
            jobCategories: p.jobCategories ? p.jobCategories.split(",") : [],
            roleType: p.roleType || "",
            payStatus: p.strPay || "",
            userSex: p.userSex || "",
            ageFrom: p.minAge || "",
            ageTo: p.maxAge || "",
            unionStatus: p.unionStatus || "",
            ethnicity: p.ethnicity ? p.ethnicity.split(",") : [],
            heightFrom: p.minHeight || "",
            heightTo: p.maxHeight || "",
            weightFrom: p.weightMin || "",
            weightTo: p.weightMax || "",
            activeCell: Boolean(p.activeCell)
          }));
        }
      } catch (err) {
        console.error("Error loading data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [userId]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleCheckboxChange = (name, val) => {
    setForm(f => {
      const arr = f[name] || [];
      const next = arr.includes(val)
        ? arr.filter(x => x !== val)
        : [...arr, val];
      return { ...f, [name]: next };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRx.test(form.userMail)) newErrors.userMail = "Invalid email";
    if (!emailRx.test(form.userMail2)) newErrors.userMail2 = "Invalid email";
    else if (form.userMail !== form.userMail2)
      newErrors.userMail2 = "Emails do not match";

    if (!/^\d{10}$/.test(form.userCell))
      newErrors.userCell = "Must be 10 digits";

    const checkRange = (from, to, key, label, max) => {
      const f = Number(form[from]), t = Number(form[to]);
      if (isNaN(f) || f < 0 || f > max) newErrors[from] = `${label} From 0–${max}`;
      if (isNaN(t) || t < 0 || t > max) newErrors[to] = `${label} To 0–${max}`;
      if (!newErrors[from] && !newErrors[to] && f >= t)
        newErrors[from] = `${label} From must be < To`;
    };

    checkRange("ageFrom", "ageTo", "age", "Age", 150);
    checkRange("weightFrom", "weightTo", "weight", "Weight", 500);
    checkRange("heightFrom", "heightTo", "height", "Height", 110);

    setErrors(newErrors);
    return !Object.keys(newErrors).length;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      ...form,
      userId: Number(userId),
      cityChoice: form.cityChoice.join(""),
      jobCategories: form.jobCategories.join(","),
      ethnicity: form.ethnicity.join(","),
      unionStatus: Array.isArray(form.unionStatus)
        ? form.unionStatus.join(",")
        : form.unionStatus,
      ageFrom: Number(form.ageFrom),
      ageTo: Number(form.ageTo),
      heightFrom: Number(form.heightFrom),
      heightTo: Number(form.heightTo),
      weightFrom: Number(form.weightFrom),
      weightTo: Number(form.weightTo),
      dateCreated: new Date().toISOString(),
      activeStatus: 1,
      activeCell: Boolean(form.activeCell),
      uid: 0,
      ipayStatus: 0,
      menSuitFrom: 0,
      menSuitTo: 0,
      menShirt: "",
      menNeckFrom: 0,
      menNeckTo: 0,
      menSleeveFrom: 0,
      menSleeveTo: 0,
      menWaistFrom: 0,
      menWaistTo: 0,
      menInseamFrom: 0,
      menInseamTo: 0,
      menShoeFrom: 0,
      menShoeTo: 0,
      womenDressFrom: 0,
      womenDressTo: 0,
      bustFrom: 0,
      bustTo: 0,
      cupSize: "",
      womenWaistFrom: 0,
      womenWaistTo: 0,
      womenHipsFrom: 0,
      womenHipsTo: 0,
      womenShoeFrom: 0,
      womenShoeTo: 0,
      childSize: "",
      shoeChildFrom: 0,
      shoeChildTo: 0,
      hairColor: ""
    };

    try {
      await api.post(
        ENDPOINTS.addOrUpdateUserNotificationPrefs(),
        payload
      );
      alert("Preferences saved successfully");
    } catch (err) {
      console.error("Save failed", err);
      alert("Failed to save");
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <>
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ pb: 4 }}>
          Role Alerts
        </Typography>
        <Grid container spacing={2}>
          {/* Emails */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Main Email"
              name="userMail"
              value={form.userMail}
              onChange={handleChange}
              error={!!errors.userMail}
              helperText={errors.userMail}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Repeat Email"
              name="userMail2"
              value={form.userMail2}
              onChange={handleChange}
              error={!!errors.userMail2}
              helperText={errors.userMail2}
            />
          </Grid>

          {/* Phone */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Cell Number"
              name="userCell"
              value={form.userCell}
              onChange={handleChange}
              error={!!errors.userCell}
              helperText={errors.userCell}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Provider</InputLabel>
              <Select
                name="cellProvider"
                value={form.cellProvider}
                onChange={handleChange}
              >
                {["Verizon", "AT&T", "T-Mobile", "Other"].map(p => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Locations */}
          <Grid item xs={12}>
            <Typography variant="h6">Job Locations</Typography>
            <FormGroup row>
              {locations.map(loc => (
                <FormControlLabel
                  key={loc.locationCode}
                  control={
                    <Checkbox
                      checked={form.cityChoice.includes(loc.locationCode)}
                      onChange={() =>
                        handleCheckboxChange("cityChoice", loc.locationCode)
                      }
                    />
                  }
                  label={loc.locationCity || loc.locationState}
                />
              ))}
            </FormGroup>
          </Grid>

          {/* Categories */}
          <Grid item xs={12}>
            <Typography variant="h6">Job Categories</Typography>
            <FormGroup row>
              {categories.map(cat => (
                <FormControlLabel
                  key={cat.jobCategoryId}
                  control={
                    <Checkbox
                      checked={form.jobCategories.includes(cat.jobCategoryName)}
                      onChange={() =>
                        handleCheckboxChange(
                          "jobCategories",
                          cat.jobCategoryName
                        )
                      }
                    />
                  }
                  label={cat.jobCategoryName}
                />
              ))}
            </FormGroup>
          </Grid>

          {/* Role / Pay / Sex */}
          {[
            {
              label: "Role Type",
              name: "roleType",
              options: ["Principal", "Background"]
            },
            {
              label: "Payment Type",
              name: "payStatus",
              options: ["Paid Only", "Not Paid", "TBD"]
            },
            {
              label: "Sex",
              name: "userSex",
              options: ["Male", "Female"]
            }
          ].map(({ label, name, options }) => (
            <Grid item xs={12} sm={4} key={name}>
              <FormControl fullWidth>
                <InputLabel>{label}</InputLabel>
                <Select
                  name={name}
                  value={form[name]}
                  onChange={handleChange}
                >
                  {options.map(opt => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ))}

          {/* Age */}
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="Age From"
              name="ageFrom"
              value={form.ageFrom}
              onChange={handleChange}
              error={!!errors.ageFrom}
              helperText={errors.ageFrom}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              label="Age To"
              name="ageTo"
              value={form.ageTo}
              onChange={handleChange}
              error={!!errors.ageTo}
              helperText={errors.ageTo}
            />
          </Grid>

          {/* Union */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Union Status</InputLabel>
              <Select
                name="unionStatus"
                value={form.unionStatus}
                onChange={handleChange}
              >
                {["AEA", "SAG-AFTRA", "Non-Union"].map(u => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Ethnicity */}
          <Grid item xs={12}>
            <Typography variant="h6">Ethnicity</Typography>
            <FormGroup row>
              {ethnicities.map(eth => (
                <FormControlLabel
                  key={eth.ethnicityId}
                  control={
                    <Checkbox
                      checked={form.ethnicity.includes(eth.ethnicityName)}
                      onChange={() =>
                        handleCheckboxChange("ethnicity", eth.ethnicityName)
                      }
                    />
                  }
                  label={eth.ethnicityName}
                />
              ))}
            </FormGroup>
          </Grid>

          {/* Height & Weight */}
          {[
            { name: "heightFrom", label: "Height From (in)", error: errors.heightFrom },
            { name: "heightTo",   label: "Height To (in)",   error: errors.heightTo   },
            { name: "weightFrom", label: "Weight From (lbs)", error: errors.weightFrom },
            { name: "weightTo",   label: "Weight To (lbs)",   error: errors.weightTo   }
          ].map(({ name, label, error }) => (
            <Grid item xs={6} sm={3} key={name}>
              <TextField
                fullWidth
                label={label}
                name={name}
                value={form[name]}
                onChange={handleChange}
                error={!!error}
                helperText={error}
              />
            </Grid>
          ))}

          {/* Save */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
            >
              Save Preferences
            </Button>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default NotificationPreferences;
